import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Filter from '@mulesoft/anypoint-components/lib/Filter';
import styles from './ScanConfigurationSearch.css';
import { ScannerStatusEnum } from '../../types/commonTypes';

// { key: 'Running', value: 'Running' },
const filterOptions = (() => {
  const options = [];

  for (const scannerStatusInfo of Object.values(ScannerStatusEnum)) {
    options.push({
      key: scannerStatusInfo.key,
      value: scannerStatusInfo.label
    });
  }

  return options;
})();

const ScanConfigurationSearch = ({
  onSearch = () => {},
  onClear = () => {},
  onFilterChange = () => {},
  searchTerm = '',
  selectedFilter = null,
  maxLength = 100
}) => {
  // Track previous searchTerm prop to detect actual prop changes
  const prevSearchTermRef = useRef(searchTerm);

  // Local state for the input value - initialize with searchTerm prop
  const [value, setValue] = useState(searchTerm);

  // Sync value state when searchTerm prop actually changes from parent
  // This mimics getDerivedStateFromProps behavior
  useEffect(() => {
    // Only update if the prop actually changed (not just a re-render)
    if (searchTerm !== prevSearchTermRef.current) {
      setValue(searchTerm);
      prevSearchTermRef.current = searchTerm;
    }
  }, [searchTerm]);

  const handleFilterChange = useCallback(
    ({ value: selectedOption }) => {
      const newFilter = selectedOption ? selectedOption.key : null;

      onFilterChange(newFilter);
    },
    [onFilterChange]
  );

  const handleClearFilters = useCallback(() => {
    onFilterChange(null);
  }, [onFilterChange]);

  const handleClearAll = useCallback(() => {
    setValue('');
    onClear();
    onFilterChange(null);
  }, [onClear, onFilterChange]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      onSearch(value.trim());
    },
    [value, onSearch]
  );

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleSubmit(event);
      }
    },
    [handleSubmit]
  );

  const handleChange = useCallback(
    ({ value: newValue, isClear }) => {
      setValue(newValue);
      if (isClear) {
        onClear();
      }
    },
    [onClear]
  );

  // Find selected option - use the exact same object reference from filterOptions
  // selectedFilter is a string (the key), so compare with option.key
  // Must be undefined (not null or {}) when no filter is selected for Filter to work correctly
  const selectedOption = useMemo(() => {
    return selectedFilter
      ? filterOptions.find((option) => option.key === selectedFilter)
      : undefined;
  }, [selectedFilter]);

  return (
    <div className={styles.searchContainer}>
      <form
        data-test-id="agent-scanners-search-form"
        className={styles.searchForm}
        onSubmit={handleSubmit}
      >
        <div className={styles.searchInputWrapper}>
          <TextField
            id="agent-scanners-search"
            aria-label="Search agent scanners"
            className={styles.searchInput}
            testId="agent-scanners-search-bar"
            name="search"
            type="search"
            placeholder="Search scanners by name or type..."
            value={value}
            maxLength={maxLength}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
          />
          <Button
            kind="tertiary"
            neutral
            onClick={handleClearAll}
            testId="clear-all-button"
            aria-label="Clear search and filters"
            className={styles.clearButton}
          >
            Clear all filters
          </Button>
        </div>
        <div className={styles.filterSection}>
          <Filter
            testId="status-filter"
            checkboxId="status-filter"
            className={styles.filter}
            aria-label="Filter by status"
            options={filterOptions}
            placeholder="All statuses"
            showClearButton={!!selectedFilter}
            selectedOption={selectedOption}
            onOptionChange={handleFilterChange}
            onClear={handleClearFilters}
          />
        </div>
      </form>
    </div>
  );
};

ScanConfigurationSearch.propTypes = {
  onSearch: PropTypes.func,
  onClear: PropTypes.func,
  onFilterChange: PropTypes.func,
  searchTerm: PropTypes.string,
  selectedFilter: PropTypes.string,
  maxLength: PropTypes.number
};

export default ScanConfigurationSearch;
